import { getImageProps } from "next/image";

import type { PushBlockContentWrapperProps } from "@jti/ui/components/PushBlockContentWrapper";
import { breakpoints } from "@jti/ui/hooks/useDeviceBreakpoint";

import type { PageConfig } from "@/context/PageConfigContext/schema";
import { BUILDER_TEXT_CLASSNAME } from "@/libs/builder/consts";

import type { PushBlockBuilderProps } from "../PushBlock";

export type PushBlockBuilderPropsWithTheme = PushBlockBuilderProps & Pick<PageConfig, "darkMode">;

export const PushBlockRenderImage: PushBlockContentWrapperProps["renderImage"] = ({ imgDesktop, imgMobile, alt }) => {
  const common = { width: 840, height: 700 };

  const {
    props: { srcSet: desktop },
  } = getImageProps({
    ...common,
    src: imgDesktop || "",
    alt: alt ?? "",
  });

  const {
    props: { srcSet: mobile, ...rest },
  } = getImageProps({
    ...common,
    src: imgMobile || imgDesktop || "",
    alt: alt ?? "",
  });

  return (
    <picture>
      <source media={`${breakpoints.desktop},${breakpoints.tablet}`} srcSet={desktop} />
      <source media={breakpoints.mobile} srcSet={mobile} />
      <img {...rest} alt={alt ?? ""} />
    </picture>
  );
};

export const mapPushBlockProps = <T extends PushBlockBuilderPropsWithTheme = PushBlockBuilderPropsWithTheme>(
  props: T
) => {
  return {
    imgDesktop: props.desktopImage,
    imgMobile: props.mobileImage,
    alt: props.alt,
    orientation: props.orientation,
    mobileOrientation: props.mobileOrientation,
    title: props.title,
    subtitle: props.subtitle,
    // Force the builder text styles in case of HTML migrated from AEM
    text: props.description ? `<div class="${BUILDER_TEXT_CLASSNAME}">${props.description}</div>` : "",
    cta: props.cta,
    smallHeightOnMobile: props.smallHeightOnMobile,
    trackingEvent: props.trackingEvent,
    containedCTA: props.containedCTA,
    href: props.href,
    renderImage: PushBlockRenderImage,
    logoProps: {
      ...props.logo,
      link: props.logo?.link || "",
      title: props.logo?.title || "",
      alt: props.logo?.alt || "",
      isDarkMode: props.darkMode,
    },
    isBackgroundOverlayEnabled: props.blockContentProperties?.isBackgroundOverlayEnabled,
    isBackgroundOverlayEnabledOnMobile: props.blockContentProperties?.isBackgroundOverlayEnabledOnMobile,
    isFullHeightContent: props.blockContentProperties?.isFullHeightContent,
    isFullWidthContent: props.blockContentProperties?.isFullWidthContent,
    blockHeight: props.blockContentProperties?.blockHeight,
    secondButton: props.secondButton,
    theme: props.theme || "inherit",
    imageFit: props.imageFit,
  };
};
